import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ColorLens as ColorLensIcon
} from '@mui/icons-material';
import { ICON_CATEGORIES, getAllIcons, getIconComponent } from '../../utils/iconLibrary';
import { useTranslation } from 'react-i18next';

/**
 * Sidebar Customizer Component
 * 
 * Sidebar customization interface with:
 * - Navigation item management
 * - Icon selection
 * - Color customization
 * - Visibility controls
 * - Custom navigation items
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Array} props.currentNavigation - Current navigation items
 * @param {Function} props.onSave - Callback when customization is saved
 * @returns {JSX.Element} The sidebar customizer component
 */
const SidebarCustomizer = ({ open, onClose, currentNavigation, onSave }) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(0);
  const [customItems, setCustomItems] = useState([]);
  const [_editingItem, _setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    id: '',
    label: '',
    icon: 'dashboard',
    path: '',
    color: '#1976d2',
    visible: true
  });

  // Load custom items from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebar_custom_items');
    if (saved) {
      try {
        setCustomItems(JSON.parse(saved));
      } catch (error) {
        void 0;
      }
    }
  }, []);

  // Save custom items
  const saveCustomItems = (items) => {
    setCustomItems(items);
    localStorage.setItem('sidebar_custom_items', JSON.stringify(items));
    if (onSave) {
      onSave(items);
    }
  };

  // Add new item
  const handleAddItem = () => {
    if (!newItem.label || !newItem.path) return;
    
    const item = {
      ...newItem,
      id: `custom_${Date.now()}`,
    };
    
    const updatedItems = [...customItems, item];
    saveCustomItems(updatedItems);
    
    // Reset form
    setNewItem({
      id: '',
      label: '',
      icon: 'dashboard',
      path: '',
      color: '#1976d2',
      visible: true
    });
  };

  // Delete item
  const handleDeleteItem = (id) => {
    const updatedItems = customItems.filter(item => item.id !== id);
    saveCustomItems(updatedItems);
  };

  // Basculer la visibility
  const handleToggleVisibility = (id) => {
    const updatedItems = customItems.map(item =>
      item.id === id ? { ...item, visible: !item.visible } : item
    );
    saveCustomItems(updatedItems);
  };

  // Render Configuration tab
  const renderConfigTab = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Add a custom element
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('sidebar.customizer.itemName')}
            value={newItem.label}
            onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
            helperText={t('sidebar.customizer.itemNameHelper')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('sidebar.customizer.path')}
            value={newItem.path}
            onChange={(e) => setNewItem({ ...newItem, path: e.target.value })}
            helperText={t('sidebar.customizer.pathHelper')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t('sidebar.customizer.icon')}</InputLabel>
            <Select
              value={newItem.icon}
              onChange={(e) => setNewItem({ ...newItem, icon: e.target.value })}
              label={t('sidebar.customizer.icon')}
            >
              {getAllIcons().map((icon) => {
                const IconComponent = icon.component;
                return (
                  <MenuItem key={icon.key} value={icon.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconComponent sx={{ fontSize: 20 }} />
                      {icon.name}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="color"
            label={t('sidebar.customizer.color')}
            value={newItem.color}
            onChange={(e) => setNewItem({ ...newItem, color: e.target.value })}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
            disabled={!newItem.label || !newItem.path}
            fullWidth
          >
            Add element
          Add</Button>
        </Grid>
      </Grid>
    </Box>
  );

  // Render custom elements tab
  const renderCustomItemsTab = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('sidebar.customizer.customItems', 'Custom items ({{count}})', { count: customItems.length })}
      </Typography>
      
      {customItems.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {t('sidebar.customizer.noCustomItems', 'No custom items. Use the Configuration tab to add some.')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {customItems.map((item) => {
            const IconComponent = getIconComponent(item.icon);
            return (
              <Grid item xs={12} key={item.id}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconComponent sx={{ color: item.color, fontSize: 24 }} />
                        <Box>
                          <Typography variant="subtitle1">{item.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.path}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={item.visible ? t('common.visible', 'Visible') : t('common.hidden', 'Hidden')}
                          color={item.visible ? 'success' : 'default'}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={item.visible ? t('common.hide', 'Hide') : t('common.show', 'Show')}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleVisibility(item.id)}
                          aria-label={t('common.action', 'Action')}
                          >
                            {item.visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteItem(item.id)}
                          aria-label={t('common.action', 'Action')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );

  // Render Preview tab
  const renderPreviewTab = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('sidebar.customizer.sidebarPreview', 'Sidebar preview')}
      </Typography>
      
      <Card variant="outlined" sx={{ maxWidth: 280, mx: 'auto' }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom color="primary">
            {t('sidebar.customizer.mainNavigation')}
          </Typography>
          
          {/* Default items */}
          {currentNavigation?.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
              <Box sx={{ width: 24, height: 24, bgcolor: 'action.selected', borderRadius: 1 }} />
              <Typography variant="body2">{item.label || t('sidebar.customizer.item', { index: index + 1 })}</Typography>
            </Box>
          ))}
          
          {customItems.filter(item => item.visible).length > 0 && (
            <>
              <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mt: 2 }}>
                {t('sidebar.customizer.customItemsLabel', 'Custom items')}
              </Typography>
              
              {customItems.filter(item => item.visible).map((item) => {
                const IconComponent = getIconComponent(item.icon);
                return (
                  <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                    <IconComponent sx={{ color: item.color, fontSize: 20 }} />
                    <Typography variant="body2">{item.label}</Typography>
                  </Box>
                );
              })}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '60vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ColorLensIcon color="primary" />
          <Typography variant="h6">{t('sidebar.customizer.title')}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label={t('common.close', 'Close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={t('sidebar.customizer.tabs.configuration')} />
          <Tab label={t('sidebar.customizer.tabs.items', { count: customItems.length })} />
          <Tab label={t('sidebar.customizer.tabs.preview')} />
        </Tabs>
      </Box>
      
      <DialogContent sx={{ p: 0, minHeight: 400 }}>
        {activeTab === 0 && renderConfigTab()}
        {activeTab === 1 && renderCustomItemsTab()}
        {activeTab === 2 && renderPreviewTab()}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{t('common.close', 'Close')}</Button>
        <Button
          variant="contained"
          onClick={() => {
            if (onSave) onSave(customItems);
            onClose();
          }}
        >
          {t('common.applyChanges', 'Apply changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SidebarCustomizer.propTypes = {};

export default React.memo(SidebarCustomizer);

